"use client";

import { createFileRoute } from "@tanstack/react-router";
import { useMotionValueEvent, useTransform } from "motion/react";
import { useState } from "react";
import { SwipingBrand } from "../components/brand/SwipingBrand";
import { CoplaneFooter } from "../components/layout/CoplaneFooter";
import { PageHeader } from "../components/layout/PageHeader";
import { RailStack } from "../components/layout/RailStack";
import { SnapSection } from "../components/layout/SnapSection";
import {
  AboutContent,
  EnterpriseReadySection,
  HeroContent,
  HowItWorksSection,
  IndexHeroAsciiOverlay,
  IterationSection
} from "../components/sections";
import {
  HeroScrollProvider,
  useHeroScroll,
} from "../context/HeroScrollContext";
import { SmoothScrollProvider } from "../context/SmoothScrollContext";
import { ViewportProvider } from "../context/ViewportContext";
import { useDocumentScrollLock } from "../hooks/useDocumentScrollLock";
import { HOME_SECTIONS } from "../lib/constants";

export const Route = createFileRoute("/")({
  component: Page,
});

function Page() {
  return (
    <ViewportProvider>
      <SmoothScrollProvider>
        <HeroScrollProvider>
          <PageContent />
        </HeroScrollProvider>
      </SmoothScrollProvider>
    </ViewportProvider>
  );
}

function PageContent() {
  const { heroRef, heroProgress } = useHeroScroll();
  useDocumentScrollLock();
  
  // Control cursor flashing based on scroll position
  // Flash only while white (heroProgress < 0.9)
  const [isFlashing, setIsFlashing] = useState(true);
  
  useMotionValueEvent(heroProgress, "change", (latest) => {
    if (latest >= 0.9 && isFlashing) {
      setIsFlashing(false);
    } else if (latest < 0.9 && !isFlashing) {
      setIsFlashing(true);
    }
  });

  // Transform nav colors based on hero progress
  // Start as Black bg / White text
  // Transition to Glass bg / Black text

  // Background: Black -> White/Glass
  const navBgColor = useTransform(heroProgress, (v) => {
    const value = typeof v === "number" ? v : 0;
    // Start transition at 0.85 (a bit earlier to ensure it's white by the time content scrolls under), end at 0.95
    const t = Math.max(0, Math.min(1, (value - 0.85) / 0.1));
    // Cubic easing in-out
    const cubicT = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    
    // Transform custom black (35 31 32) to white (255 255 255)
    const r = Math.round(35 * (1 - cubicT) + 255 * cubicT);
    const g = Math.round(31 * (1 - cubicT) + 255 * cubicT);
    const b = Math.round(32 * (1 - cubicT) + 255 * cubicT);
    
    // Alpha: 1 -> 0.8 (Glass)
    const a = 1 - (0.2 * cubicT);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  });

  // Text: White -> Black
  const navTextColor = useTransform(heroProgress, (v) => {
    const value = typeof v === "number" ? v : 0;
    const t = Math.max(0, Math.min(1, (value - 0.85) / 0.1));
    const cubicT = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    
    // White (255) to custom black (35 31 32)
    const r = Math.round(255 * (1 - cubicT) + 35 * cubicT);
    const g = Math.round(255 * (1 - cubicT) + 31 * cubicT);
    const b = Math.round(255 * (1 - cubicT) + 32 * cubicT);
    return `rgb(${r}, ${g}, ${b})`;
  });

  // Border: Transparent -> Visible (fading in)
  const navBorderColor = useTransform(heroProgress, (v) => {
     const value = typeof v === "number" ? v : 0;
     if (value < 0.85) return "rgba(229, 229, 229, 0)";
     
     const t = Math.max(0, Math.min(1, (value - 0.85) / 0.1));
     const cubicT = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
     
     return `rgba(229, 229, 229, ${0.5 * cubicT})`;
  }) as any;

  const navStyles = {
    backgroundColor: navBgColor,
    borderColor: navBorderColor,
    textColor: navTextColor,
    // Hover states
    // When black (start): hover white text, slightly lighter black bg
    // When white (end): hover black text, slightly darker white bg
    // This is complex to do perfectly with simple values, so we'll stick to a safe middle ground or
    // just let the textColor handle the main interaction feedback.
    hoverTextColor: navTextColor, 
    hoverBgColor: "rgba(128, 128, 128, 0.2)", // Works on both
    activeBgColor: "rgba(128, 128, 128, 0.3)",
    activeTextColor: navTextColor,
  };

  const logoColor = useTransform(heroProgress, (v) => {
    const value = typeof v === "number" ? v : 0;
    // Start transition at 0.85 (a bit earlier to ensure it's white by the time content scrolls under), end at 0.95
    const t = Math.max(0, Math.min(1, (value - 0.85) / 0.1));
    // Cubic easing in-out
    const cubicT = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    // Interpolate between white (255 255 255) and custom black (35 31 32)
    const r = Math.round(255 * (1 - cubicT) + 35 * cubicT);
    const g = Math.round(255 * (1 - cubicT) + 31 * cubicT);
    const b = Math.round(255 * (1 - cubicT) + 32 * cubicT);
    return `rgb(${r}, ${g}, ${b})`;
  });

  const renderSectionContent = (sectionId: string, label: string) => {
    switch (sectionId) {
      case "ABOUT":
        return <AboutContent />;
      case "HOW_IT_WORKS":
        return <HowItWorksSection />;
      case "ENTERPRISE_READY":
        return <EnterpriseReadySection />;
      case "ITERATION":
        return <IterationSection />;
      default:
        return (
          <h2 className="text-2xl font-serif text-gray-800">
            {label}
          </h2>
        );
    }
  };

  return (
    <div
      id="page"
      className="relative w-screen min-h-screen overflow-x-hidden bg-white"
    >
      <IndexHeroAsciiOverlay />
      <RailStack sections={HOME_SECTIONS} />
      <header className="relative bg-transparent pointer-events-none">
        <PageHeader 
          logo={<SwipingBrand height={20} flashing={isFlashing} color={logoColor} />} 
          navStyles={navStyles}
          hideHamburgerUntil={heroProgress}
        />
      </header>
      <main className="relative z-1 flex flex-col text-black">
        <SnapSection id="HERO" sectionRef={heroRef}>
          <HeroContent />
        </SnapSection>
        {HOME_SECTIONS.filter((section) => section.id !== "HERO").map(
          (section) => (
            <SnapSection
            key={section.id}
            id={section.id}
          >
              {renderSectionContent(section.id, section.label)}
            </SnapSection>
          )
        )}
      </main>
      <CoplaneFooter />
    </div>
  );
}
